import { useCallback, useEffect, useState } from "react";
import {
  Banner,
  Button,
  Checkbox,
  ckConfirm,
  Dialog,
  Field,
  OptionChips,
  Panel,
  SearchInput,
  Segmented,
  SimpleTable,
  Tag,
  TextArea,
  TextInput,
  useCkToast,
} from "@NP-Webber/crm-kit/dist/next/index.js";
import { mailingApi, type SubscriberDto, type SuppressionDto } from "../../api/mailingApi";
import { useShell } from "../shell/ShellContext";
import { errorMessage, fmtDateTime, fmtNum } from "./mailingUi";

const PAGE = 50;
const SOURCE_LABEL: Record<string, string> = { form: "טופס הרשמה", manual: "ידני", import: "ייבוא" };

function Pager({ page, total, onPage }: { page: number; total: number; onPage: (p: number) => void }) {
  if (total <= PAGE) return null;
  return (
    <div className="ml-row ml-row--center ml-pager">
      <Button size="sm" icon="right" disabled={page <= 1} onClick={() => onPage(page - 1)}>
        הקודם
      </Button>
      <span className="ml-muted">
        {fmtNum((page - 1) * PAGE + 1)}–{fmtNum(Math.min(page * PAGE, total))} מתוך {fmtNum(total)}
      </span>
      <Button size="sm" icon="left" disabled={page * PAGE >= total} onClick={() => onPage(page + 1)}>
        הבא
      </Button>
    </div>
  );
}

/** Paste addresses → subscribers (with a consent declaration) or the suppression list. */
function AddDialog({ kind, open, onClose, onDone }: { kind: "subscribers" | "suppressions"; open: boolean; onClose: () => void; onDone: () => void }) {
  const toast = useCkToast();
  const [text, setText] = useState("");
  const [note, setNote] = useState("");
  const [source, setSource] = useState<"manual" | "import">("manual");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) {
      setText("");
      setNote("");
      setConsent(false);
    }
  }, [open]);

  const submit = async () => {
    setBusy(true);
    try {
      if (kind === "subscribers") {
        const r = await mailingApi.subscribers.add({ text, source, note });
        toast.success(`נוספו ${fmtNum(r.added)} מנויים`);
      } else {
        const r = await mailingApi.suppressions.add({ text, note });
        toast.success(`נחסמו ${fmtNum(r.added)} כתובות`);
      }
      onDone();
      onClose();
    } catch (e) {
      toast.error(errorMessage(e, "ההוספה נכשלה"));
    } finally {
      setBusy(false);
    }
  };

  const subs = kind === "subscribers";
  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="md"
      icon={subs ? "users" : "x"}
      title={subs ? "הוספת מנויים" : "חסימת כתובות"}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            ביטול
          </Button>
          <Button variant="primary" onClick={() => void submit()} disabled={busy || !text.trim() || (subs && !consent)}>
            {busy ? "רגע…" : subs ? "הוספה" : "חסימה"}
          </Button>
        </>
      }
    >
      <div className="ml-stack">
        <Field label="כתובות" hint='כתובת בכל שורה, אפשר עם שם: "דוד כהן, david@example.org". אפשר להדביק עמודה מאקסל.' full>
          <TextArea rows={8} dir="auto" value={text} onChange={(e) => setText(e.target.value)} autoFocus />
        </Field>
        {subs ? (
          <OptionChips
            ariaLabel="מקור"
            options={[
              { value: "manual", label: "הוספה ידנית" },
              { value: "import", label: "ייבוא מרשימה קיימת" },
            ]}
            value={source}
            onChange={(v) => setSource(v as "manual")}
          />
        ) : null}
        <Field label="הערה (רשות)" hint={subs ? "למשל: נרשמו בכנס 2026" : "למשל: ביקש בטלפון"}>
          <TextInput value={note} onChange={(e) => setNote(e.target.value)} maxLength={300} />
        </Field>
        {subs ? (
          <Checkbox
            checked={consent}
            onChange={setConsent}
            label="אני מאשר/ת שבעלי הכתובות הסכימו לקבל דיוור מהארגון (חוק התקשורת §30א)"
          />
        ) : null}
      </div>
    </Dialog>
  );
}

function Subscribers() {
  const toast = useCkToast();
  const { allowed } = useShell();
  const canManage = allowed("mailing.manage");
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState<{ items: SubscriberDto[]; total: number } | null>(null);
  const [adding, setAdding] = useState(false);

  const load = useCallback(() => {
    mailingApi.subscribers
      .list({ status, q, page, pageSize: PAGE })
      .then(setData)
      .catch((e) => toast.error(errorMessage(e, "לא הצלחנו לטעון את המנויים")));
  }, [status, q, page, toast]);
  useEffect(() => {
    const t = window.setTimeout(load, 200);
    return () => window.clearTimeout(t);
  }, [load]);

  const remove = async (s: SubscriberDto) => {
    if (!(await ckConfirm({ title: "להסיר את המנוי?", message: `${s.email} לא יופיע יותר ברשימת המנויים. (כדי שלא יקבל דיוור בכלל — חסמו אותו.)`, confirmLabel: "הסרה", danger: true }))) return;
    try {
      await mailingApi.subscribers.remove(s.id);
      load();
    } catch (e) {
      toast.error(errorMessage(e, "ההסרה נכשלה"));
    }
  };

  return (
    <Panel
      title="מנויים"
      icon="users"
      sub="מי שהסכים לקבל דיוור: נרשם בטופס ואישר, או שהוספתם עם הסכמה"
      flush
      actions={canManage ? <Button size="sm" icon="plus" onClick={() => setAdding(true)}>הוספה</Button> : undefined}
    >
      <div className="ml-table-tools">
        <Segmented
          ariaLabel="מצב"
          value={status}
          onChange={(v) => {
            setStatus(v);
            setPage(1);
          }}
          items={[
            { value: "", label: "הכול" },
            { value: "confirmed", label: "מאושרים" },
            { value: "pending", label: "ממתינים לאישור" },
          ]}
        />
        <SearchInput
          value={q}
          onChange={(v) => {
            setQ(v);
            setPage(1);
          }}
          placeholder="חיפוש…"
        />
      </div>
      <SimpleTable
        ariaLabel="מנויים"
        loading={!data}
        rows={data?.items ?? []}
        rowKey="id"
        empty="אין מנויים"
        columns={[
          { key: "email", label: "מייל", ltr: true },
          { key: "name", label: "שם", render: (r: SubscriberDto) => r.name || "—" },
          { key: "status", label: "מצב", render: (r: SubscriberDto) => (r.status === "confirmed" ? <Tag tone="success">מאושר</Tag> : <Tag tone="info">ממתין לאישור</Tag>) },
          { key: "source", label: "מקור", render: (r: SubscriberDto) => r.formTitle || SOURCE_LABEL[r.source] || r.source },
          { key: "consentedAt", label: "נרשם", render: (r: SubscriberDto) => fmtDateTime(r.confirmedAt ?? r.consentedAt) },
          ...(canManage
            ? [{ key: "x", label: "", width: 48, render: (r: SubscriberDto) => <Button size="sm" variant="ghost" icon="trash" iconOnly aria-label="הסרה" onClick={() => void remove(r)} /> }]
            : []),
        ]}
      />
      <Pager page={page} total={data?.total ?? 0} onPage={setPage} />
      <AddDialog kind="subscribers" open={adding} onClose={() => setAdding(false)} onDone={load} />
    </Panel>
  );
}

function Suppressions() {
  const toast = useCkToast();
  const { allowed } = useShell();
  const canManage = allowed("mailing.manage");
  const [reason, setReason] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState<{ items: SuppressionDto[]; total: number; counts: Record<string, number> } | null>(null);
  const [adding, setAdding] = useState(false);

  const load = useCallback(() => {
    mailingApi.suppressions
      .list({ reason, q, page, pageSize: PAGE })
      .then(setData)
      .catch((e) => toast.error(errorMessage(e, "לא הצלחנו לטעון את רשימת החסומים")));
  }, [reason, q, page, toast]);
  useEffect(() => {
    const t = window.setTimeout(load, 200);
    return () => window.clearTimeout(t);
  }, [load]);

  const remove = async (s: SuppressionDto) => {
    const self = s.reason === "unsubscribe";
    const ok = await ckConfirm({
      title: "להחזיר את הכתובת לדיוור?",
      message: self
        ? `${s.email} הסיר/ה את עצמו/ה מרשימת התפוצה. מותר להחזיר רק אם ביקש/ה זאת במפורש.`
        : `${s.email} יוכל/תוכל לקבל שוב דיוור מהארגון.`,
      confirmLabel: self ? "ביקש/ה במפורש — להחזיר" : "החזרה",
      danger: self,
    });
    if (!ok) return;
    try {
      await mailingApi.suppressions.remove(s.id, self);
      load();
    } catch (e) {
      toast.error(errorMessage(e, "הפעולה נכשלה"));
    }
  };

  const counts = data?.counts ?? {};
  return (
    <Panel
      title="חסומים"
      icon="x"
      sub="כתובות שלא יקבלו דיוור: מי שהסיר את עצמו, וכתובות שחסמתם. כתובות שלא קיימות או שדיווחו על ספאם נחסמות אוטומטית."
      flush
      actions={canManage ? <Button size="sm" icon="plus" onClick={() => setAdding(true)}>חסימה</Button> : undefined}
    >
      <div className="ml-table-tools">
        <Segmented
          ariaLabel="סיבה"
          value={reason}
          onChange={(v) => {
            setReason(v);
            setPage(1);
          }}
          items={[
            { value: "", label: "הכול" },
            { value: "unsubscribe", label: "הסרה עצמית", count: counts.unsubscribe },
            { value: "manual", label: "חסימה ידנית", count: counts.manual },
          ]}
        />
        <SearchInput
          value={q}
          onChange={(v) => {
            setQ(v);
            setPage(1);
          }}
          placeholder="חיפוש כתובת…"
        />
      </div>
      <SimpleTable
        ariaLabel="חסומים"
        loading={!data}
        rows={data?.items ?? []}
        rowKey="id"
        empty="אין כתובות חסומות"
        columns={[
          { key: "email", label: "מייל", ltr: true },
          { key: "reasonLabel", label: "סיבה" },
          { key: "note", label: "הערה", render: (r: SuppressionDto) => r.note || "" },
          { key: "createdAt", label: "מתי", render: (r: SuppressionDto) => fmtDateTime(r.createdAt) },
          ...(canManage
            ? [{ key: "x", label: "", width: 90, render: (r: SuppressionDto) => <Button size="sm" variant="ghost" onClick={() => void remove(r)}>החזרה</Button> }]
            : []),
        ]}
      />
      <Pager page={page} total={data?.total ?? 0} onPage={setPage} />
      <AddDialog kind="suppressions" open={adding} onClose={() => setAdding(false)} onDone={load} />
    </Panel>
  );
}

/** "נמענים" tab — subscribers (consents) and the suppression list. */
export function MailingLists() {
  const [view, setView] = useState<"subscribers" | "suppressions">("subscribers");
  return (
    <div className="ml-stack">
      <Segmented
        ariaLabel="רשימה"
        value={view}
        onChange={(v) => setView(v as "subscribers")}
        items={[
          { value: "subscribers", label: "מנויים" },
          { value: "suppressions", label: "חסומים" },
        ]}
      />
      {view === "subscribers" ? <Subscribers /> : <Suppressions />}
      <Banner tone="info">
        לקוחות לא צריכים להיות מנויים כדי לקבל דיוור — אפשר לשלוח לכל לקוח עם מייל. הסרה בקישור שבתחתית המייל חוסמת את הכתובת בכל דיוור עתידי.
      </Banner>
    </div>
  );
}
